import {Box, Button, Divider, Stack, Typography} from '@mui/material';
import Cookies from 'js-cookie';
import React, {Fragment, useState} from 'react';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AlertActions,
	AlertModal,
	CTAButtonFixed,
	MainFooter,
	StyledCard,
	StyledGrid,
	TagChip,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {RequestInfo} from 'presentation/pages/private/refund/components';
import {InvoiceTypeEnum} from 'presentation/pages/private/refund/constants/invoiceTypeEnum';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {formatPriceRefund, formatText} from 'presentation/utils';

import {BankInfoCard} from '../../../Request/components/Conference/components/BankInfoCard';
import {BeneficiaryInfoCard} from '../../../Request/components/Conference/components/BeneficiaryInfoCard';
import {useAccordionExpandedStore} from '../../../Request/store/useAccordionExpandedStore';
import {useStepStore} from '../../../Request/store/useStepStore';
import {useSubmitStatusStore} from '../../../Request/store/useSubmitStatusStore';
import {useTreatmentDataStore} from '../../../Request/store/useTreatmentDataStore';
import {AccordionExpandedEnum, StepEnum, SubmitStatusEnum} from '../../../Request/types';
import {useRefundDocumentsStore} from '../../store/useRefundDocumentsStore';
import {INVALID_DOC_CONFIRM, REVIEW_ATTACHMENTS, STATUS_BADGE_META} from '../SendDocuments/constants';
import {RefundDocumentRow, RefundDocumentStatusEnum} from '../SendDocuments/types';
import {CustomContainer, StyledFooterContainer} from './styles';
import {ConferenceV2Props} from './types';

const {totalReceiptValue, finishRequest, continueForNewRequest, confirmModalityModal} =
	translations['pt-br'].privateFlow.refund.conferencePage;
const {requestsLabel} = translations['pt-br'].commons;

// TODO: revisar layout visual com design quando spec do novo fluxo estiver definida
export const Conference = ({handleNextStep, isLoading}: ConferenceV2Props) => {
	const [openModalityModalState, setOpenModalityModalState] = useState(false);
	const [invalidDocName, setInvalidDocName] = useState<string | null>(null);
	const {isMobile, isLaptop} = useMediaQueries();
	const {handleCreateNewRequestInProtocol, setSelectedRequestIndex} = useRefundRequest();

	const treatmentDataState = useTreatmentDataStore(state => state.data);
	const documentRows = useRefundDocumentsStore(state => state.rows);

	const uploadedDocuments = documentRows.filter(row => Boolean(row.anexo));
	const firstReprovedDoc = uploadedDocuments.find(
		row => row.status === RefundDocumentStatusEnum.REJEITADO,
	);

	const {requests, handleResetStates} = useRefundRequest();
	const setStep = useStepStore(state => state.setStep);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);

	const setSubmitStatus = useSubmitStatusStore(state => state.setStatus);

	const totalValue: number =
		requests.requestData?.reduce(
			(acc, item) => acc + parseFloat(item.financialData.invoiceAmount.replace(',', '.')),
			0,
		) || 0;

	const handleAddNewRequest = () => {
		handleCreateNewRequestInProtocol();
		setSubmitStatus(SubmitStatusEnum.ADD_NEW_REQUEST_IN_PROTOCOL);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setOpenModalityModalState(false);
		handleResetStates();
		Cookies.remove('uploadFilesTerm');
	};

	const handleEditProviderData = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setAccordionExpanded(AccordionExpandedEnum.PROVIDER_DATA);
	};

	const handleEditPatientData = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setAccordionExpanded(AccordionExpandedEnum.PATIENT_DATA);
	};

	const handleEditFiscalDocument = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setAccordionExpanded(AccordionExpandedEnum.FISCAL_DOCUMENT);
	};

	const handleEditAttachment = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.SEND_DOCUMENTS);
	};

	const handleReplaceFromReview = () => {
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.SEND_DOCUMENTS);
	};

	const handleOpenAttachmentFromReview = (row: RefundDocumentRow) => {
		if (row.anexoUrl) window.open(row.anexoUrl, '_blank', 'noopener,noreferrer');
	};

	const handleFinalize = () => {
		if (firstReprovedDoc) {
			setInvalidDocName(firstReprovedDoc.documento);
			return;
		}
		handleNextStep();
	};

	const handleCloseInvalidDocModal = () => setInvalidDocName(null);

	const handleConfirmModality = () => {
		setOpenModalityModalState(true);
	};

	return (
		<Fragment>
			<CustomContainer>
				<StyledGrid.Root spacing={3}>
					<StyledGrid.Item md={12} lg={8}>
						<Stack spacing={2}>
							{requests.requestData ? (
								<Stack spacing={2}>
									<Typography variant="subtitle2" color="text.secondary">
										{requestsLabel}
									</Typography>
									<Stack spacing={2}>
										{requests.requestData.map((item, index) => {
											const isCNPJ =
												item.professionalData.professionalType === 'J';

											const showRequestNumber =
												requests.requestData.length >= 2;

											return (
												<Box key={item.requestCode}>
													<RequestInfo
														isSingleItem={
															requests.requestData.length === 1
														}
														city={formatText(
															item.financialData.invoiceIssueCity,
														)}
														state={item.financialData.invoiceIssueState}
														requestNumber={
															showRequestNumber
																? index + 1
																: undefined
														}
														patientName={
															formatText(item.beneficiaryName) || ''
														}
														doctorName={formatText(
															item.professionalData.professionalName,
														)}
														providerName={
															isCNPJ
																? formatText(
																		item.professionalData
																			?.providerName,
																	)
																: '-'
														}
														specialtyName={formatText(
															item.treatmentData.specialty
																.description,
														)}
														date={item.treatmentData.date.replaceAll(
															'-',
															'/',
														)}
														numFiscalDocument={String(
															item.financialData.invoiceNumber,
														)}
														numFiscalDigit={
															item.financialData.invoiceDigit
														}
														value={formatPriceRefund(
															item.financialData.invoiceAmount,
														)}
														hasNfe={
															item.financialData.invoiceType ===
															InvoiceTypeEnum.EMIT
														}
														handleClickProviderData={() =>
															handleEditProviderData(index)
														}
														handleClickPatientData={() =>
															handleEditPatientData(index)
														}
														handleClickFiscalDocument={() =>
															handleEditFiscalDocument(index)
														}
														handleClickAttachment={() =>
															handleEditAttachment(index)
														}
													/>
												</Box>
											);
										})}
									</Stack>
								</Stack>
							) : null}

							{uploadedDocuments.length > 0 ? (
								<Stack spacing={2}>
									<Typography
										fontWeight={600}
										variant="subtitle2"
										color="text.secondary">
										{REVIEW_ATTACHMENTS.sectionTitle}
									</Typography>
									<StyledCard.Container>
										<Stack divider={<Divider />}>
											{uploadedDocuments.map(row => {
												const badge = STATUS_BADGE_META[row.status];
												const isReproved =
													row.status === RefundDocumentStatusEnum.REJEITADO;
												return (
													<Stack key={row.id} gap={1} py={1}>
														<Stack
															direction="row"
															alignItems="center"
															gap={3}
															justifyContent={
																isMobile ? 'space-between' : undefined
															}>
															<TagChip
																label={badge.label}
																color={badge.color}
																sx={{
																	width: 75,
																	justifyContent: 'center',
																	'& .MuiChip-label': {
																		fontSize: 12,
																		px: 0,
																	},
																}}
															/>
															<Button
																type="button"
																variant="text"
																size="small"
																onClick={() =>
																	handleOpenAttachmentFromReview(
																		row,
																	)
																}
																endIcon={
																	<Icon
																		name="external-link-default"
																		size={16}
																	/>
																}>
																{REVIEW_ATTACHMENTS.viewDocument}
															</Button>
															{isReproved && !isMobile ? (
																<Button
																	type="button"
																	variant="text"
																	size="small"
																	onClick={
																		handleReplaceFromReview
																	}
																	sx={{ml: 'auto'}}>
																	{
																		REVIEW_ATTACHMENTS.replaceDocument
																	}
																</Button>
															) : null}
														</Stack>
														{isReproved && isMobile ? (
															<Button
																type="button"
																variant="outlined"
																size="medium"
																fullWidth
																onClick={handleReplaceFromReview}>
																{REVIEW_ATTACHMENTS.replaceDocument}
															</Button>
														) : null}
													</Stack>
												);
											})}
										</Stack>
									</StyledCard.Container>
								</Stack>
							) : null}

							<BankInfoCard />

							{isLaptop ? <BeneficiaryInfoCard /> : null}

							<CTAButtonFixed.Root justifyContent="space-between">
								<Stack
									direction="row"
									flex={1}
									gap={1}
									alignItems="center"
									justifyContent={isMobile ? 'space-between' : 'flex-end'}>
									{requests.requestData ? (
										<Fragment>
											<Typography variant="subtitle1" color="text.primary">
												{totalReceiptValue}
											</Typography>
											<Typography variant="h5" color="text.primary">
												{new Intl.NumberFormat('pt-Br', {
													style: 'currency',
													currency: 'BRL',
												}).format(totalValue)}
											</Typography>
										</Fragment>
									) : null}
								</Stack>

								<Stack
									flex={0}
									gap={1}
									justifyContent="flex-end"
									flexDirection={isMobile ? 'column' : 'row'}>
									<CTAButtonFixed.Action
										data-tag-id={
											RefundTaggingEnum.CLICK_CADASTRAR_OUTRA_SOLICITACAO
										}
										size="large"
										variant="outlined"
										onClick={handleConfirmModality}
										fullWidth={isMobile}>
										{continueForNewRequest}
									</CTAButtonFixed.Action>

									<CTAButtonFixed.Action
										data-tag-id={
											RefundTaggingEnum.CLICK_FINALIZAR_SOLICITACAO_REEMBOLSO
										}
										disabled={isLoading}
										size="large"
										onClick={handleFinalize}
										variant="contained"
										fullWidth={isMobile}
										endIcon={
											isLoading ? <Loading size={20} color="inherit" /> : null
										}>
										{finishRequest}
									</CTAButtonFixed.Action>
								</Stack>
							</CTAButtonFixed.Root>
						</Stack>
					</StyledGrid.Item>
					{!isLaptop ? (
						<StyledGrid.Item md={12} lg={4}>
							<Stack spacing={2}>
								<BeneficiaryInfoCard />
							</Stack>
						</StyledGrid.Item>
					) : null}
				</StyledGrid.Root>
			</CustomContainer>

			<AlertModal
				headerDataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_TIPO_ATENDIMENTO}
				closeBtnDataTagId={RefundTaggingEnum.CLICK_FECHAR_MODAL_TIPO_ATENDIMENTO}
				variant="info"
				title={confirmModalityModal.title}
				open={openModalityModalState}
				onClose={() => setOpenModalityModalState(false)}
				headerTitle={confirmModalityModal.headerTitle}
				subDescription={
					<Stack gap={0} textAlign="center">
						<Typography>
							{confirmModalityModal.description1}
							<strong>{treatmentDataState.modality.toLowerCase()}</strong>.
						</Typography>
						<Typography>{confirmModalityModal.description2}</Typography>
					</Stack>
				}
				footerContent={
					<Stack gap={1} sx={{width: '100%'}}>
						<Button
							data-tag-id={RefundTaggingEnum.CLICK_VOLTAR_PARA_REVISAO_REEMBOLSO}
							onClick={() => setOpenModalityModalState(false)}
							variant="outlined"
							size="large"
							fullWidth>
							{confirmModalityModal.button1}
						</Button>
						<Button
							data-tag-id={
								RefundTaggingEnum.CLICK_PROSSEGUIR_NOVA_SOLICITACAO_REEMBOLSO
							}
							onClick={handleAddNewRequest}
							variant="contained"
							size="large"
							fullWidth>
							{confirmModalityModal.button2}
						</Button>
					</Stack>
				}
			/>
			<AlertModal
				open={invalidDocName !== null}
				onClose={handleCloseInvalidDocModal}
				headerTitle={INVALID_DOC_CONFIRM.headerTitle}
				variant="info"
				customIconName="info-filled"
				title={INVALID_DOC_CONFIRM.title}
				description={
					<Typography variant="body2" textAlign="center">
						{INVALID_DOC_CONFIRM.descriptionBefore}
						<Typography component="span" variant="body2" fontWeight={700}>
							{invalidDocName ?? ''}
						</Typography>
						{INVALID_DOC_CONFIRM.descriptionAfter}
					</Typography>
				}
				footerContent={
					<AlertActions
						primaryAction={handleCloseInvalidDocModal}
						primaryText={INVALID_DOC_CONFIRM.confirm}
					/>
				}
			/>

			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
};
